import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  Avatar, 
  Container,
  Checkbox,
  Paper,
  Button,
  Tooltip,
  TablePagination
} from '@mui/material';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { 
  GetNotification, 
  ChangeNotificationStatus, 
  ChangeAllNotificationStatus 
} from '../../Redux/Actions/userStatusService';

// Create our own time formatting function
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHrs / 24);

  if (diffMins < 60) {
    return `${diffMins} mins ago`;
  } else if (diffHrs < 24) {
    return `${diffHrs} hr ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else {
    return `${diffDays} days ago`;
  }
};

function AllNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch = useDispatch();
  const userInfoString = localStorage.getItem("userInfo");
  const { users } = useSelector((state) => state.userStatus);
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const role = userInfo?.portal || 'MANAGER';
  const entityId = localStorage.getItem("entId");

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await dispatch(GetNotification({ 
        role: role,
        entityId: entityId
      }));
      if (response?.payload?.data) {
        const sortedNotifications = sortNotifications(response.payload.data);
        setNotifications(sortedNotifications);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const sortNotifications = (notifs) => {
    if (!notifs) return [];
    
    return [...notifs].sort((a, b) => {
      if (!a.isRead && b.isRead) return -1;
      if (a.isRead && !b.isRead) return 1;
      
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await dispatch(ChangeAllNotificationStatus());
      if (response?.payload?.statusCode === 200) {
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Function to determine which icon to display based on notification type
  const getNotificationIcon = (notification) => {
    if (notification.isRead) {
      return <CheckIcon sx={{ color: 'white' }} />;
    } else {
      return <SouthEastIcon sx={{ color: 'white' }} />;
    }
  };

  const handleCheckboxChange = async (notification) => {
    try {
      const isRead = !notification.isRead;
      const response = await dispatch(ChangeNotificationStatus({ 
        id: notification._id, 
        body: { isRead } 
      }));

      if (response?.payload?.statusCode === 200) {
        setNotifications(prevNotifications => {
          const updatedNotifications = prevNotifications.map(item => 
            item._id === notification._id ? { ...item, isRead } : item
          );
          return sortNotifications(updatedNotifications);
        });
      }
    } catch (error) {
      console.error("Error changing notification status:", error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const unreadCount = notifications?.filter(notification => !notification?.isRead)?.length;
  
  // Get current page notifications
  const currentNotifications = notifications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            You have {unreadCount} unread notifications
          </Typography>
        </Box>
        
        {unreadCount > 0 && (
          <Tooltip title="Mark all as read">
            <Button 
              variant="outlined" 
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
            >
              Mark all as read
            </Button>
          </Tooltip>
        )}
      </Box>
      
      <Paper 
        sx={{ 
          width: '100%', 
          borderRadius: 2, 
          overflow: 'hidden',
          boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          '& .MuiList-root': {
            padding: 0,
          }
        }}
      >
        <List disablePadding>
          {currentNotifications.map((notification, index) => (
            <React.Fragment key={notification._id || index}>
              <ListItem 
                alignItems="flex-start" 
                sx={{ 
                  py: 2, 
                  px: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                  ...(notification.isRead === false && {
                    bgcolor: '#f0f3fd',
                  }),
                }}
                onClick={() => handleCheckboxChange(notification)}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: '#2196f3',
                    width: 40, 
                    height: 40 
                  }}
                >
                  {getNotificationIcon(notification)}
                </Avatar>
                
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'medium' }}>
                    {notification.title || 'Notification'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {notification.message || notification.body || 'No message available'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px', textAlign: 'right' }}>
                    {notification.createdAt ? formatTimeAgo(notification.createdAt) : '5 mins ago'}
                  </Typography>
                  
                  <Checkbox
                    checked={notification.isRead}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCheckboxChange(notification);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                  />
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
          
          {notifications.length === 0 && (
            <ListItem>
              <Typography variant="body1" sx={{ py: 4, textAlign: 'center', width: '100%' }}>
                No notifications found
              </Typography>
            </ListItem>
          )}
        </List>
        
        {notifications.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <TablePagination
              component="div"
              count={notifications.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Notifications per page:"
              SelectProps={{
                inputProps: { 'aria-label': 'Notifications per page' },
                native: false,
              }}
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default AllNotifications;